import React, { useEffect, useRef, lazy, Suspense } from "react";
import {
  Routes,
  Route,
  Navigate,
  useNavigate,
  Outlet,
  useLocation,
} from "react-router-dom";
import { UserProvider, useUser } from "./context/auth";
import {
  PermissionProvider,
  usePermissions,
} from "./context/PermissionContext";
import { setSessionExpiredCallback } from "./utils/sessionManager";
import SessionTimeoutManager from "./components/SessionTimeoutManager";

// import UserMvdDashboard from "./pages/reports/user-mvd-reports/UserMVDDashboard";
import "./assets/css/global.css";
import { SearchProvider } from "./context/SearchContext";

const RegistrationReports = lazy(
  () => import("./pages/reports/RegistrationReports"),
);
const VolumeTrendAnalysisReports = lazy(
  () => import("./pages/reports/VolumeTrendAnalysisReports"),
);
const MaterialSpecificReport = lazy(
  () => import("./pages/reports/MaterialSpecificReport"),
);
const MVDReports = lazy(() => import("./pages/reports/mvd-reports/MvdReport"));
const ComplianceFeesReport = lazy(
  () => import("./pages/reports/compliance-reports/ComplianceFeesReport"),
);
const UserMVDDashboard = lazy(
  () => import("./pages/reports/user-mvd-reports/UserMVDDashboard"),
);
const MemberRenewalAnalytics = lazy(
  () => import("./pages/reports/member-registration/MemberRenewalAnalytics"),
);
const Toaster = lazy(() =>
  import("./components/ui/toaster").then((module) => ({
    default: module.Toaster,
  })),
);
const RootLayout = lazy(() => import("./layouts/rootLayout"));
const MFAChallenge = lazy(() => import("./pages/Auth/MFAChallenge"));
const Login = lazy(() => import("./pages/Auth/LoginPage"));
const ForgotPassword = lazy(() => import("./pages/Auth/ForgotPasswordPage"));
const ResetPassword = lazy(() => import("./pages/Auth/ResetPasswordPage"));
const OAuthCallback = lazy(() => import("./pages/Auth/OAuthCallback"));
const SetPassword = lazy(() => import("./pages/Auth/SetPassword"));
const CreateNotificationForm = lazy(
  () => import("./pages/notifications/notifications-form"),
);
const EditNotification = lazy(
  () => import("./pages/notifications/notification-edit"),
);
const MFASetup = lazy(() => import("./pages/account-settings/MFASetup"));
const PasswordChange = lazy(
  () => import("./pages/account-settings/PasswordChange"),
);
const UserManagement = lazy(
  () => import("./pages/account-settings/UserManagement"),
);
const RoleManagement = lazy(
  () => import("./pages/account-settings/RoleManagement"),
);
const ModuleManagement = lazy(
  () => import("./pages/account-settings/ModuleManagement"),
);
const ModuleEditPage = lazy(
  () => import("./pages/account-settings/ModuleEditPage"),
);
const RoleEditPage = lazy(
  () => import("./pages/account-settings/RoleEditPage"),
);
const ModuleAddPage = lazy(
  () => import("./pages/account-settings/ModuleAddPage"),
);
const ModuleDetailsPage = lazy(
  () => import("./pages/account-settings/ModuleDetailsPage"),
);
const RoleAddPage = lazy(() => import("./pages/account-settings/RoleAddPage"));
const RoleDetailsPage = lazy(
  () => import("./pages/account-settings/RoleDetailsPage"),
);
const ProfileAddForm = lazy(
  () => import("./pages/company-profile/ProfileAddForm"),
);
const RegistrationAddForm = lazy(
  () => import("./pages/registrations/registration-form"),
);
const RegistrationDetailsPage = lazy(
  () => import("./pages/registrations/registration-detailsPage"),
);
const RegistrationEditForm = lazy(
  () => import("./pages/registrations/registration-edit"),
);
const RenewalAddForm = lazy(
  () => import("./pages/member-renewals/renewalsAddForm"),
);
const MembershipRenewalView = lazy(
  () => import("./pages/member-renewals/renewalsDetailsPage"),
);
const WasteStreamsTable = lazy(
  () => import("./pages/waste-stream/streams-table"),
);
const WasteStreamView = lazy(() => import("./pages/waste-stream/streams-view"));
const WasteStreamEdit = lazy(() => import("./pages/waste-stream/streams-edit"));
const CompanyProfileView = lazy(
  () => import("./pages/company-profile/ProfileDetailsPage"),
);
const CompanyProfileEditForm = lazy(
  () => import("./pages/company-profile/ProfileEditForm"),
);
const ProductCatalogPage = lazy(
  () => import("./pages/company-profile/ProductCatalogPage"),
);
const MembershipCategoriesTable = lazy(
  () => import("./pages/member-category/category-table"),
);
const PackagingSubcategoriesTable = lazy(
  () => import("./pages/packaging-subcategory/packaging-subcategory-table"),
);
const MvdForm = lazy(() => import("./pages/mvd/mvd-form"));
const MvdEditForm = lazy(() => import("./pages/mvd/mvd-Edit-form"));
const EprSubcategoryFeesTable = lazy(
  () => import("./pages/epr-subcategory/epr-subcategory"),
);
const ProofOfPaymentAddForm = lazy(
  () => import("./pages/proof-of-payment/payments-add"),
);
const MvdDetailsPage = lazy(() => import("./pages/mvd/mvd-detailsPage"));
const RegistrationSubmissionPage = lazy(
  () => import("./pages/registrations/registration-submission"),
);
const ProofOfPaymentView = lazy(
  () => import("./pages/proof-of-payment/payment-view"),
);
const ProofOfPaymentEditForm = lazy(
  () => import("./pages/proof-of-payment/payment-edit"),
);
const MemberRenewalEditForm = lazy(
  () => import("./pages/member-renewals/renewalsEditForm"),
);
const Notification = lazy(() => import("./pages/notifications/notifications"));
const ChangeRequestDetailsPage = lazy(
  () => import("./pages/change-requests/ChangeRequestDetailsPage"),
);
const ChangeRequestForm = lazy(
  () => import("./pages/change-requests/ChangeRequestForm"),
);
const ProfileSubmissionPage = lazy(
  () => import("./pages/company-profile/profile-submission"),
);
const QuarterlyCompliance = lazy(
  () => import("./pages/quarterly-compliance/quarterly-compliance-detailspage"),
);
const NotificationDetailsPage = lazy(
  () => import("./pages/notifications/notification-detailspage"),
);
const AccountSettings = lazy(
  () => import("./pages/account-settings/AccountSettings"),
);
const RoleInValueChainTable = lazy(
  () => import("./pages/role-in-value-chain/role-in-value-chain-table"),
);
const MemberDirectory = lazy(() => import("./pages/public/MemberDirectory"));
const PublicLanding = lazy(() => import("./pages/public/PublicLanding"));
const StatusTable = lazy(() => import("./pages/status/status-table"));
const TransitionTable = lazy(
  () => import("./pages/transitions/transition-table"),
);
const DocumentsPage = lazy(() => import("./pages/documents/DocumentsPage"));

const IndustryCategoryTable = lazy(
  () => import("./pages/industry-category/industry-category-table"),
);

const AdminFaqsPage = lazy(() => import("./pages/admin/faqs/AdminFaqsPage"));
const HelpPage = lazy(() => import("./pages/help/HelpPage"));

const ProfileTable = lazy(() => import("./pages/company-profile/profileTable"));
const RegistrationsTable = lazy(
  () => import("./pages/registrations/registrations-table"),
);
const RenewalsTable = lazy(
  () => import("./pages/member-renewals/renewalTable"),
);
const MvdTable = lazy(() => import("./pages/mvd/mvd-table"));
const ProofOfPaymentsTable = lazy(
  () => import("./pages/proof-of-payment/payments_table"),
);
const QuarterlyComplianceTable = lazy(
  () => import("./pages/quarterly-compliance/quarterly-compliance-table"),
);
const NotificationsTable = lazy(
  () => import("./pages/notifications/notifications-table"),
);
const ChangeRequestTable = lazy(
  () => import("./pages/change-requests/changeRequestTable"),
);
const AdminDashboard = lazy(
  () => import("./pages/dashboard/admin-dashboard/AdminDashboard"),
);
const UserDashboard = lazy(
  () => import("./pages/dashboard/userdashboard/user-dashboard"),
);
const WebsiteLayout = lazy(() => import("./layouts/websiteLayout"));
const CompanyProfileLayout = lazy(
  () => import("./layouts/companyProfileLayout"),
);

const renderLazy = (Component) => (
  <Suspense
    fallback={
      <div className="h-[40vh] flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
        <p className="mt-3 text-gray-600">Loading page...</p>
      </div>
    }
  >
    <Component />
  </Suspense>
);

const renderLazyNode = (node) => (
  <Suspense
    fallback={
      <div className="h-[40vh] flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
        <p className="mt-3 text-gray-600">Loading page...</p>
      </div>
    }
  >
    {node}
  </Suspense>
);

const adminPermission = (modules, actions = ["readAny"]) => ({
  module: modules,
  actions,
});

const expandRouteActions = (actions = []) => {
  const actionList = Array.isArray(actions) ? actions : [actions];
  const expanded = new Set(
    actionList.map((action) => String(action || "").trim()).filter(Boolean),
  );

  const promote = (anyAction, ownAction, baseAction) => {
    if (expanded.has(anyAction)) {
      expanded.add(ownAction);
      expanded.add(baseAction);
    }
  };

  promote("readAny", "readOwn", "read");
  promote("createAny", "createOwn", "create");
  promote("updateAny", "updateOwn", "update");
  promote("deleteAny", "deleteOwn", "delete");

  return Array.from(expanded);
};

// Protected Route component to handle authorization
const ProtectedRoute = ({
  element,
  requiredRole,
  fallbackPath,
  requiredPermission,
}) => {
  const { canDoAny, isLoadingPermissions } = usePermissions();

  if (isLoadingPermissions) {
    return (
      <div className="h-[40vh] flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
        <p className="mt-3 text-gray-600">Loading permissions...</p>
      </div>
    );
  }

  if (requiredPermission && requiredPermission.actions) {
    const modules = Array.isArray(requiredPermission.module)
      ? requiredPermission.module
      : [requiredPermission.module];
    const hasAccess = modules
      .filter(Boolean)
      .some((moduleName) =>
        canDoAny(moduleName, expandRouteActions(requiredPermission.actions)),
      );

    if (!hasAccess) {
      return <Navigate to={fallbackPath || "/"} replace />;
    }
  }

  // `requiredRole` is retained for backward compatibility but no longer enforced
  // as hardcoded role checks block permission-based custom roles.
  return element;
};

const DashboardRouter = () => {
  const { currentUser } = useUser();
  const { canDoExact, isLoadingPermissions } = usePermissions();

  if (isLoadingPermissions) {
    return (
      <div className="h-[40vh] flex flex-col items-center justify-center">
        <div className="h-10 w-10 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
        <p className="mt-3 text-gray-600">Loading dashboard...</p>
      </div>
    );
  }

  // Company/member accounts should always use the member dashboard.
  const userType = String(currentUser?.type || "").toLowerCase();
  if (userType === "company") {
    return renderLazy(UserDashboard);
  }

  // Admin dashboard should only be selected for privileged scopes/surfaces.
  const canAccessAdminSurfaces =
    canDoExact("admin-dashboard", "readAny") ||
    canDoExact("admin_dashboard", "readAny") ||
    canDoExact("account-settings", "readAny") ||
    canDoExact("account_settings", "readAny") ||
    canDoExact("users", "readAny") ||
    canDoExact("roles", "readAny") ||
    canDoExact("modules", "readAny") ||
    canDoExact("system-settings", "readAny") ||
    canDoExact("system_settings", "readAny");

  const shouldUseAdminDashboard = canAccessAdminSurfaces;
  return shouldUseAdminDashboard
    ? renderLazy(AdminDashboard)
    : renderLazy(UserDashboard);
};

const AppRoutes = () => {
  const { isUserAuthenticated, loading, currentUser, mfaVerified } = useUser();
  const location = useLocation();

  const isPublicRoute =
    location.pathname.startsWith("/public") ||
    location.pathname.startsWith("/client/member-registration") ||
    ["/login", "/forgot-password", "/set-password", "/reset-password", "/oauth-callback", "/registration-success"].includes(
      location.pathname,
    );

  // Only block authenticated routes while checking authentication.
  if (loading && !isPublicRoute) {
    return (
      <div className="h-screen flex flex-col items-center justify-center">
        <div className="h-12 w-12 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
        <p className="mt-4 text-gray-600">Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes - always accessible */}
      <Route
        path="/login"
        element={
          isUserAuthenticated ? (
            <Navigate
              to={currentUser?.mfaEnabled && !mfaVerified ? "/mfa" : "/"}
              replace
            />
          ) : (
            renderLazy(Login)
          )
        }
      />
      <Route
        path="/forgot-password"
        element={
          isUserAuthenticated ? (
            <Navigate to="/" replace />
          ) : (
            renderLazy(ForgotPassword)
          )
        }
      />
      <Route
        path="/set-password"
        element={
          isUserAuthenticated ? (
            <Navigate to="/" replace />
          ) : (
            renderLazyNode(<SetPassword title="Set Password" />)
          )
        }
      />
      <Route
        path="/reset-password"
        element={
          isUserAuthenticated ? (
            <Navigate to="/" replace />
          ) : (
            renderLazy(ResetPassword)
          )
        }
      />
      <Route path="/oauth-callback" element={renderLazy(OAuthCallback)} />
      <Route
        path="/client/member-registration"
        element={renderLazy(WebsiteLayout)}
      />
      <Route
        path="/client/member-registration#"
        element={renderLazy(WebsiteLayout)}
      />
      <Route path="/public" element={renderLazy(PublicLanding)} />
      <Route
        path="/registration-success"
        element={renderLazy(RegistrationSubmissionPage)}
      />
      <Route path="/public/members" element={renderLazy(MemberDirectory)} />

      {/* Authenticated routes */}
      {isUserAuthenticated ? (
        <>
          {/* MFA challenge must remain accessible even when not verified */}
          <Route path="/mfa" element={renderLazy(MFAChallenge)} />

          {/* Wrap the rest of the app with an MFA gate */}
          <Route element={<RequireMFA />}>
            <Route path="/test" element={<div>Test Page</div>} />
            <Route
              path="/client/company-profile/:id/edit"
              element={renderLazy(CompanyProfileLayout)}
            />
            <Route
              path="/registration-success"
              element={renderLazy(RegistrationSubmissionPage)}
            />
            <Route
              path="/profile-update-success"
              element={renderLazy(ProfileSubmissionPage)}
            />
            <Route
              path="/client/notifications"
              element={renderLazy(Notification)}
            />
            {/* Main app layout */}
            <Route path="/" element={renderLazy(RootLayout)}>
              {/* Dynamic dashboard based on user role */}
              <Route index element={<DashboardRouter />} />
              <Route path="/dashboard" element={<DashboardRouter />} />
              {/* Admin-only routes */}
              <Route
                path="/company-profile"
                element={
                  <ProtectedRoute
                    element={renderLazy(ProfileTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      [
                        "company-profiles",
                        "company_profiles",
                        "companyprofiles",
                        "company-profile",
                        "company_profile",
                        "profiles",
                      ],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/company-profiles"
                element={
                  <ProtectedRoute
                    element={renderLazy(ProfileTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      [
                        "company-profiles",
                        "company_profiles",
                        "companyprofiles",
                        "company-profile",
                        "company_profile",
                        "profiles",
                      ],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/company-profiles/create"
                element={
                  <ProtectedRoute
                    element={renderLazyNode(
                      <ProfileAddForm isEditMode={false} />,
                    )}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      [
                        "company-profiles",
                        "company_profiles",
                        "companyprofiles",
                        "profiles",
                      ],
                      ["createAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/waste-streams"
                element={
                  <ProtectedRoute
                    element={renderLazy(WasteStreamsTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["waste-streams", "waste_streams", "wastestreams"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/waste-streams/:id"
                element={
                  <ProtectedRoute
                    element={renderLazy(WasteStreamView)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["waste-streams", "waste_streams", "wastestreams"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/waste-streams/:id/edit"
                element={
                  <ProtectedRoute
                    element={renderLazy(WasteStreamEdit)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["waste-streams", "waste_streams", "wastestreams"],
                      ["updateAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/member-categories"
                element={
                  <ProtectedRoute
                    element={renderLazy(MembershipCategoriesTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      [
                        "member-categories",
                        "member_categories",
                        "membercategories",
                      ],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/roles-in-value-chain"
                element={
                  <ProtectedRoute
                    element={renderLazy(RoleInValueChainTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      [
                        "roles-in-value-chain",
                        "roles_in_value_chain",
                        "roleinvaluechain",
                      ],
                      ["readAny"]
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/packaging-subcategories"
                element={
                  <ProtectedRoute
                    element={renderLazy(PackagingSubcategoriesTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      [
                        "packaging-subcategories",
                        "packaging_subcategories",
                        "packagingsubcategories",
                      ],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/epr-subcategory-fees"
                element={
                  <ProtectedRoute
                    element={renderLazy(EprSubcategoryFeesTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      [
                        "epr-subcategory-fees",
                        "epr_subcategory_fees",
                        "eprsubcategoryfees",
                      ],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/statuses"
                element={
                  <ProtectedRoute
                    element={renderLazy(StatusTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["statuses", "status"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/transitions"
                element={
                  <ProtectedRoute
                    element={renderLazy(TransitionTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["transitions", "transition"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/industry-category"
                element={
                  <ProtectedRoute
                    element={renderLazy(IndustryCategoryTable)}
                    requiredRole="admin"
                    // requiredPermission={adminPermission(
                    //   ["industry-category", "industry_category"],
                    //   ["readAny"],
                    // )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/settings/faqs"
                element={
                  <ProtectedRoute
                    element={renderLazy(AdminFaqsPage)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(["faqs"], ["readAny"])}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/registrations"
                element={
                  <ProtectedRoute
                    element={renderLazy(RegistrationsTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      [
                        "registrations",
                        "registration",
                        "member-registrations",
                        "member_registrations",
                        "memberregistrations",
                      ],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/member-registrations"
                element={
                  <ProtectedRoute
                    element={renderLazy(RegistrationsTable)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      [
                        "registrations",
                        "registration",
                        "member-registrations",
                        "member_registrations",
                        "memberregistrations",
                      ],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/notifications/create"
                element={
                  <ProtectedRoute
                    element={renderLazy(CreateNotificationForm)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["notifications", "notification"],
                      ["createAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/notifications/:id/edit"
                element={
                  <ProtectedRoute
                    element={renderLazy(EditNotification)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["notifications", "notification"],
                      ["updateAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/reports/mvd"
                element={
                  <ProtectedRoute
                    element={renderLazy(MVDReports)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["reports", "reporting"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/reports/compliance-fees-reports"
                element={
                  <ProtectedRoute
                    element={renderLazy(ComplianceFeesReport)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["reports", "reporting"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/reports/registration-reports"
                element={
                  <ProtectedRoute
                    element={renderLazy(RegistrationReports)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["reports", "reporting"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/reports/volume-trend-reports"
                element={
                  <ProtectedRoute
                    element={renderLazy(VolumeTrendAnalysisReports)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["reports", "reporting"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/reports/material-specific-reports"
                element={
                  <ProtectedRoute
                    element={renderLazy(MaterialSpecificReport)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["reports", "reporting"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />

              {/* Routes accessible to both admin and regular users */}
              <Route
                path="/company-profiles/:id"
                element={renderLazy(CompanyProfileView)}
              />
              <Route
                path="/company-profiles/:id/edit"
                element={renderLazy(CompanyProfileEditForm)}
              />
              <Route
                path="/company-profiles/:companyProfileId/product-catalog/:catalogId?"
                element={
                  <ProtectedRoute
                    element={renderLazy(ProductCatalogPage)}
                    requiredPermission={{
                      module: ["productcatalogs", "product_catalogs", "companyproductcatalogs"],
                      actions: ["createAny", "createOwn", "readAny", "readOwn", "updateAny", "updateOwn"],
                    }}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/change-requests"
                element={renderLazy(ChangeRequestTable)}
              />
              <Route
                path="/change-request/:id"
                element={renderLazy(ChangeRequestDetailsPage)}
              />
              <Route
                path="/change-request/create"
                element={renderLazy(ChangeRequestForm)}
              />
              <Route path="/mvd/create" element={renderLazy(MvdForm)} />
              <Route path="/mvd/:id" element={renderLazy(MvdDetailsPage)} />
              <Route path="/mvd" element={renderLazy(MvdTable)} />
              <Route path="/mvd/:id/edit" element={renderLazy(MvdEditForm)} />
              <Route
                path="/quarterly-compliance"
                element={renderLazy(QuarterlyComplianceTable)}
              />
              <Route
                path="/quarterly-compliance/:id"
                element={renderLazy(QuarterlyCompliance)}
              />
              <Route
                path="/notifications"
                element={renderLazy(NotificationsTable)}
              />
              <Route
                path="/notifications/:id"
                element={renderLazy(NotificationDetailsPage)}
              />
              <Route
                path="/registrations/create"
                element={renderLazy(RegistrationAddForm)}
              />
              <Route
                path="/registrations/:id"
                element={renderLazy(RegistrationDetailsPage)}
              />
              <Route
                path="/registrations/:id/edit"
                element={renderLazy(RegistrationEditForm)}
              />
              <Route
                path="/member-registrations/create"
                element={renderLazy(RegistrationAddForm)}
              />
              <Route
                path="/member-registrations/:id"
                element={renderLazy(RegistrationDetailsPage)}
              />
              <Route
                path="/member-registrations/:id/edit"
                element={renderLazy(RegistrationEditForm)}
              />
              <Route path="/renewals" element={renderLazy(RenewalsTable)} />
              <Route
                path="/renewals/create"
                element={renderLazy(RenewalAddForm)}
              />
              <Route
                path="/renewals/:id"
                element={renderLazy(MembershipRenewalView)}
              />
              <Route
                path="/renewals/:id/edit"
                element={renderLazy(MemberRenewalEditForm)}
              />
              <Route
                path="/member-renewals"
                element={renderLazy(RenewalsTable)}
              />
              <Route
                path="/member-renewals/create"
                element={renderLazy(RenewalAddForm)}
              />
              <Route
                path="/member-renewals/:id"
                element={renderLazy(MembershipRenewalView)}
              />
              <Route
                path="/member-renewals/:id/edit"
                element={renderLazy(MemberRenewalEditForm)}
              />
              <Route
                path="/proof-of-payments"
                element={renderLazy(ProofOfPaymentsTable)}
              />
              <Route
                path="/proof-of-payments/create"
                element={renderLazy(ProofOfPaymentAddForm)}
              />
              <Route
                path="/proof-of-payments/:id/edit"
                element={renderLazy(ProofOfPaymentEditForm)}
              />
              <Route
                path="/proof-of-payments/:id"
                element={renderLazy(ProofOfPaymentView)}
              />
              <Route
                path="/payments"
                element={renderLazy(ProofOfPaymentsTable)}
              />
              <Route
                path="/payments/create"
                element={renderLazy(ProofOfPaymentAddForm)}
              />
              <Route
                path="/payments/:id/edit"
                element={renderLazy(ProofOfPaymentEditForm)}
              />
              <Route
                path="/payments/:id"
                element={renderLazy(ProofOfPaymentView)}
              />

              {/* User-specific routes (aliases) */}
              <Route
                path="/my-profile"
                element={renderLazy(CompanyProfileView)}
              />
              <Route
                path="/my-registration"
                element={renderLazy(RegistrationDetailsPage)}
              />
              <Route
                path="/my-registration/:id"
                element={renderLazy(RegistrationDetailsPage)}
              />
              <Route path="/my-renewals" element={renderLazy(RenewalsTable)} />
              <Route
                path="/my-renewals/create"
                element={renderLazy(RenewalAddForm)}
              />
              <Route
                path="/reports/mvd-user-reports"
                element={renderLazy(UserMVDDashboard)}
              />
              <Route
                path="/my-payments"
                element={renderLazy(ProofOfPaymentsTable)}
              />
              <Route
                path="/my-payments/create"
                element={renderLazy(ProofOfPaymentAddForm)}
              />
              <Route
                path="/my-payments/:id"
                element={renderLazy(ProofOfPaymentView)}
              />
              <Route path="/my-mvds/create" element={renderLazy(MvdForm)} />
              <Route path="/my-mvds/:id" element={renderLazy(MvdDetailsPage)} />
              <Route
                path="/my-quarterly-compliance"
                element={renderLazy(QuarterlyComplianceTable)}
              />
              <Route
                path="/documents"
                element={renderLazy(DocumentsPage)}
              />
              <Route
                path="/account-settings"
                element={renderLazy(AccountSettings)}
              />
              <Route
                path="/account-settings/roles"
                element={
                  <ProtectedRoute
                    element={renderLazy(RoleManagement)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["roles", "role"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/dashboard/roles"
                element={
                  <ProtectedRoute
                    element={renderLazy(RoleManagement)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["roles", "role"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/account-settings/roles/:id/edit"
                element={
                  <ProtectedRoute
                    element={renderLazy(RoleEditPage)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["roles", "role"],
                      ["updateAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/account-settings/users"
                element={
                  <ProtectedRoute
                    element={renderLazy(UserManagement)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["users", "user", "user-management", "user_management"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route path="/account-settings/mfa" element={<MFARoute />} />
              <Route
                path="/account-settings/password"
                element={renderLazy(PasswordChange)}
              />
              <Route
                path="/reports/member-renewals"
                element={renderLazy(MemberRenewalAnalytics)}
              />
              <Route
                path="/account-settings/modules"
                element={
                  <ProtectedRoute
                    element={renderLazy(ModuleManagement)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["modules", "module"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/account-settings/modules/create"
                element={
                  <ProtectedRoute
                    element={renderLazy(ModuleAddPage)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["modules", "module"],
                      ["createAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/account-settings/modules/:id/edit"
                element={
                  <ProtectedRoute
                    element={renderLazy(ModuleEditPage)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["modules", "module"],
                      ["updateAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/account-settings/modules/:id"
                element={
                  <ProtectedRoute
                    element={renderLazy(ModuleDetailsPage)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["modules", "module"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route path="/help" element={renderLazy(HelpPage)} />
              <Route
                path="/account-settings/roles/create"
                element={
                  <ProtectedRoute
                    element={renderLazy(RoleAddPage)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["roles", "role"],
                      ["createAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
              <Route
                path="/account-settings/roles/:id"
                element={
                  <ProtectedRoute
                    element={renderLazy(RoleDetailsPage)}
                    requiredRole="admin"
                    requiredPermission={adminPermission(
                      ["roles", "role"],
                      ["readAny"],
                    )}
                    fallbackPath="/"
                  />
                }
              />
            </Route>
          </Route>
        </>
      ) : (
        // If not authenticated, redirect all other routes to login
        <Route path="*" element={<Navigate to="/login" replace />} />
      )}

      {/* Catch-all route for authenticated users - redirect to dashboard */}
      {isUserAuthenticated && (
        <Route path="*" element={<Navigate to="/" replace />} />
      )}
    </Routes>
  );
};

// Simple wrapper to inject navigation into MFASetup back handler
const MFARoute = () => {
  const navigate = useNavigate();
  return (
    <Suspense
      fallback={
        <div className="h-[40vh] flex flex-col items-center justify-center">
          <div className="h-10 w-10 rounded-full border-4 border-blue-500 border-t-transparent animate-spin" />
          <p className="mt-3 text-gray-600">Loading page...</p>
        </div>
      }
    >
      <MFASetup onBack={() => navigate("/account-settings")} />
    </Suspense>
  );
};

// Gate component that handles password change requirement first, then MFA
const RequireMFA = () => {
  const { currentUser, mfaVerified, mustChangePassword } = useUser();
  const location = useLocation();

  // First check if password change is required
  // console.log('RequireMFA gate: mustChangePassword =', mustChangePassword, 'pathname =', location.pathname);
  if (
    mustChangePassword &&
    location.pathname !== "/account-settings/password"
  ) {
    console.log("RequireMFA gate: Redirecting to password change");
    return (
      <Navigate
        to="/account-settings/password"
        state={{ from: location }}
        replace
      />
    );
  }

  // Then check MFA requirement
  const needsMfa = currentUser?.mfaEnabled === true && mfaVerified !== true;
  if (needsMfa && location.pathname !== "/mfa") {
    return <Navigate to="/mfa" state={{ from: location }} replace />;
  }

  return <Outlet />;
};

const EXCLUDED_SESSION_ROUTES = [
  "/client/member-registration",
  "/login",
  "/forgot-password",
  "/reset-password",
  "/set-password",
  "/oauth-callback",
];

// Wrapper component to use hooks that depend on router context
function AppContent() {
  const isHandlingSessionExpiry = useRef(false);

  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useUser();
  const isExcludedRoute = EXCLUDED_SESSION_ROUTES.some((route) =>
    location.pathname.startsWith(route),
  );

  useEffect(() => {
    if (location.pathname === "/login") {
      isHandlingSessionExpiry.current = false;
    }
  }, [location.pathname]);

  useEffect(() => {
    setSessionExpiredCallback(async () => {
      if (isExcludedRoute || isHandlingSessionExpiry.current) return;

      isHandlingSessionExpiry.current = true;
      await logout({ skipApi: true, delay: false });
      navigate("/login", { replace: true, state: { sessionExpired: true } });
    });
  }, [isExcludedRoute, logout, navigate]);

  const handleSessionTimeout = async (reason) => {
    if (isHandlingSessionExpiry.current) return;

    isHandlingSessionExpiry.current = true;
    // When another tab signed out, the server session is already gone; this tab only
    // needs to catch up.
    await logout({ skipApi: reason === "elsewhere", delay: false });
    navigate("/login", {
      replace: true,
      state: reason === "idle" ? { sessionExpired: true } : undefined,
    });
  };

  return (
    <>
      <Suspense fallback={null}>
        <Toaster />
      </Suspense>
      <SessionTimeoutManager onExpired={handleSessionTimeout} />
      <AppRoutes />
    </>
  );
}

function App() {
  return (
    <UserProvider>
      <PermissionProvider>
        <SearchProvider>
          <AppContent />
        </SearchProvider>
      </PermissionProvider>
    </UserProvider>
  );
}

export default App;
